import { useMutation } from '@tanstack/react-query'
import { KeyRound, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/api/auth'
import { gqlRequest, graphqlErrorMessage } from '@/lib/api/client'
import { CreateAccessTokenMutation } from '@/lib/api/operations'
import { PatLoginDialog } from './pat-login-dialog'

export function LoginForm({ onSuccess }: { onSuccess?: () => void }) {
  const { mode, signIn } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [patLoginOpen, setPatLoginOpen] = useState(false)

  const login = useMutation({
    mutationFn: async () => {
      const data = await gqlRequest({ mode }, CreateAccessTokenMutation, {
        username: username.trim(),
        password,
      })
      const accessToken = data.createAccessToken
      if (!accessToken?.token) throw new Error('Invalid username or password.')
      return accessToken
    },
    onSuccess: (accessToken) => {
      // Prefer the non-expiring parse token so kitchen screens stay signed in.
      const parseToken = accessToken.parseToken ?? null
      signIn({
        mode,
        token: parseToken ?? accessToken.token,
        expires: parseToken ? null : (accessToken.expires ?? null),
        user: accessToken.user ?? null,
      })
      onSuccess?.()
    },
  })

  return (
    <>
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (!login.isPending) login.mutate()
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="username">Användarnamn</Label>
          <Input id="username" autoComplete="username" autoCapitalize="none" value={username} onChange={(event) => setUsername(event.target.value)} required />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Lösenord</Label>
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
        </div>

        {login.isError ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {graphqlErrorMessage(login.error)}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <Button type="submit" className="flex-1" size="lg" disabled={login.isPending}>
            {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Logga in
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground"
            title="Logga in med åtkomsttoken"
            aria-label="Logga in med åtkomsttoken"
            onClick={() => setPatLoginOpen(true)}
            disabled={login.isPending}
          >
            <KeyRound />
          </Button>
        </div>
      </form>
      {patLoginOpen ? <PatLoginDialog onClose={() => setPatLoginOpen(false)} onSuccess={onSuccess} /> : null}
    </>
  )
}
